import type { NavigateFunction } from "react-router-dom";

import { authPath } from "./auth-helpers";
import {
  type ClerkFailure,
  clerkFailureMessage,
  MFA_UNAVAILABLE,
  PROTECT_RETRY,
} from "./clerk-errors";
import { markCodeSent } from "./code-sent";
import { followDecorated } from "./follow-auth";

interface ActiveTask {
  key?: string;
}

interface SignInAttempt {
  finalize: (params?: {
    navigate?: (params: {
      decorateUrl: (url: string) => string;
      session: { currentTask?: ActiveTask | null };
    }) => void;
  }) => Promise<{ error: ClerkFailure | null }>;
  mfa: {
    sendEmailCode: () => Promise<{ error: ClerkFailure | null }>;
  };
  status:
    | "complete"
    | "needs_client_trust"
    | "needs_first_factor"
    | "needs_identifier"
    | "needs_new_password"
    | "needs_protect_check"
    | "needs_second_factor";
}

export const finishSignIn = async (
  signIn: SignInAttempt,
  navigate: NavigateFunction
): Promise<string | null> => {
  switch (signIn.status) {
    case "complete": {
      const { error } = await signIn.finalize({
        navigate: ({ decorateUrl, session }) => {
          if (session.currentTask?.key === "choose-organization") {
            followDecorated(
              decorateUrl,
              authPath("/session-tasks/choose-organization")
            );
            return;
          }
          if (session.currentTask?.key === "reset-password") {
            followDecorated(
              decorateUrl,
              authPath("/session-tasks/reset-password")
            );
            return;
          }
          followDecorated(decorateUrl);
        },
      });
      return clerkFailureMessage(error);
    }
    case "needs_client_trust": {
      const { error } = await signIn.mfa.sendEmailCode();
      if (error) {
        return clerkFailureMessage(error);
      }
      markCodeSent("device-trust");
      navigate(authPath("/login/verify"));
      return null;
    }
    case "needs_second_factor":
      return MFA_UNAVAILABLE;
    case "needs_new_password":
      navigate(authPath("/login/reset/password"));
      return null;
    case "needs_protect_check":
      return PROTECT_RETRY;
    case "needs_first_factor":
      return null;
    case "needs_identifier":
      navigate(authPath("/login"), { replace: true });
      return null;
    default: {
      const unreachable: never = signIn.status;
      return unreachable;
    }
  }
};
